import { useMemo, useState } from 'react'
import { Plus, Receipt } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { SectionLoader } from '@/components/common/SectionLoader'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { Badge, Button, ConfirmDialog, Select } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { formatCurrency } from '@/lib/formatters'
import { downloadMyClaimReceipt } from '@/api/services/expense-claims.service'
import { useMyClaims, useWithdrawClaim } from '../hooks/useExpenseClaims'
import { CLAIM_STATUS_LABEL, CLAIM_STATUS_VARIANT, payoutLabel } from '../utils/claimDisplay'
import type { ClaimStatus, ExpenseClaimView } from '../types/expenseClaim.types'
import { ClaimFormModal } from './ClaimFormModal'
import { ClaimReceipts, ClaimSteps } from './ClaimParts'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  ...(Object.keys(CLAIM_STATUS_LABEL) as ClaimStatus[]).map((s) => ({
    value: s,
    label: CLAIM_STATUS_LABEL[s],
  })),
]

const LIMIT = 10

function formatDate(value?: string): string {
  if (!value) return '—'
  try {
    return format(parseISO(value), 'd MMM yyyy')
  } catch {
    return '—'
  }
}

/** Staff self-service: submit claims with receipts and see when they are paid back. */
export function MyExpensesPage(): React.ReactElement {
  const { can } = usePermissions()
  const canSubmit = can('expenses', 'create_self')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [formOpen, setFormOpen] = useState(false)
  const [withdrawing, setWithdrawing] = useState<ExpenseClaimView | null>(null)
  const withdrawMutation = useWithdrawClaim()

  const params = useMemo(
    () => ({ ...(status ? { status: status as ClaimStatus } : {}), page, limit: LIMIT }),
    [status, page]
  )
  const { data, isLoading, isError, error, refetch } = useMyClaims(params)
  const claims = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Expenses' }]}
      icon={Receipt}
      title="My Expenses"
      description="Expense claims you submitted, their approval, and when they are paid back."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            canSubmit ? (
              <Button onClick={() => setFormOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                New claim
              </Button>
            ) : undefined
          }
        >
          <div className="w-full sm:w-45">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(v) => {
                setStatus(v)
                setPage(1)
              }}
            />
          </div>
        </WorkspaceToolbar>

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={6} rows={5} label="Loading your claims" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load your expense claims"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : claims.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No expense claims yet"
            description="Spent your own money on work? Submit a claim with the receipt and it is paid back with your salary once approved."
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Claim</DataTableHeadCell>
                <DataTableHeadCell>Company</DataTableHeadCell>
                <DataTableHeadCell align="right">Amount</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Approval</DataTableHeadCell>
                <DataTableHeadCell>Receipts</DataTableHeadCell>
                <DataTableHeadCell align="right">
                  <span className="sr-only">Actions</span>
                </DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {claims.map((c) => (
                <DataTableRow key={c.id}>
                  <DataTableCell>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{c.title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {c.categoryLabel} · {formatDate(c.date)}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {c.companyName}
                    </span>
                  </DataTableCell>
                  <DataTableCell align="right">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(c.amount, c.currency)}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="space-y-1">
                      <Badge variant={CLAIM_STATUS_VARIANT[c.status]}>
                        {CLAIM_STATUS_LABEL[c.status]}
                      </Badge>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {payoutLabel(c.payout)}
                      </div>
                      {c.status === 'rejected' && c.rejectedReason && (
                        <div className="max-w-56 text-xs text-red-600 dark:text-red-400">
                          {c.rejectedReason}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <ClaimSteps steps={c.approval.steps} />
                  </DataTableCell>
                  <DataTableCell>
                    <ClaimReceipts
                      receipts={c.receipts}
                      download={(uploadId) => downloadMyClaimReceipt(c.id, uploadId)}
                    />
                  </DataTableCell>
                  <DataTableCell align="right">
                    {c.status === 'submitted' && canSubmit && (
                      <Button variant="ghost" size="sm" onClick={() => setWithdrawing(c)}>
                        Withdraw
                      </Button>
                    )}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <ClaimFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <ConfirmDialog
        open={withdrawing !== null}
        onClose={() => setWithdrawing(null)}
        onConfirm={() => {
          if (!withdrawing) return
          withdrawMutation.mutate(withdrawing.id, { onSettled: () => setWithdrawing(null) })
        }}
        title="Withdraw this claim?"
        message="It leaves the approvers' inbox and will not be paid. You can submit a new claim later."
        confirmLabel="Withdraw"
        variant="danger"
        confirmLoading={withdrawMutation.isPending}
      />
    </PageShell>
  )
}

MyExpensesPage.displayName = 'MyExpensesPage'
